import type { FC } from "react";
import { useState } from "react";
import type { UUID } from "../../../../utils/uuid";
import type { EmailMoment } from "../../../email-moment/types";
import type { Tags } from "../../../merge-tag/types";
import type { SerialisedLexicalEditorDesign } from "../../../message/editor/types/serialised-editor-design";
import type { ToTarget } from "../../../to/to-target";
import type { FollowUpChannelWithData, FollowUpSchemaType } from "../../reducer/schema";
import { isChatChannelWithData, isEmailChannelWithData, isSmsChannelWithData } from "../../utils";
import { FollowUpBodySection } from "../follow-up-body/follow-up-body-section";
import type { FollowUpMessageChatProps } from "./follow-up-message-chat";
import { FollowUpMessageChat } from "./follow-up-message-chat";
import type { FollowUpMessageEmailProps } from "./follow-up-message-email";
import { FollowUpMessageEmail } from "./follow-up-message-email";
import { FollowUpMessageEmailNewsletter } from "./follow-up-message-email-newsletter";
import { FollowUpMessageImport } from "./follow-up-message-import";
import { FollowUpMessageSms } from "./follow-up-message-sms";

interface FollowUpMessageProps {
  original_moment_id: UUID | null;
  moment_id: UUID;
  channel: FollowUpChannelWithData;
  mergeTags: Tags;
  toTarget: ToTarget;
  setDesign: (val: SerialisedLexicalEditorDesign) => void;
  setChannelData: (data: FollowUpChannelWithData) => void;
  design: FollowUpSchemaType["design"];
  useLexicalEditor: boolean;
  originalMomentUseLexicalEditor: boolean;
  emailMoment: EmailMoment | null;
  onEmailMomentChange: (emailMoment: EmailMoment) => void;
  restrictedEmailFrom: UUID[];
  dynamicSegmentIdMap: Record<string, string> | null;
  onDynamicSegmentIdMapChange: (map: Record<string, string> | null) => void;
  errors: FollowUpMessageChatProps["errors"] & FollowUpMessageEmailProps["errors"];
  disabled: boolean;
}

export const FollowUpMessage: FC<FollowUpMessageProps> = ({
  original_moment_id,
  moment_id,
  channel,
  setChannelData,
  mergeTags,
  toTarget,
  setDesign,
  design,
  useLexicalEditor,
  originalMomentUseLexicalEditor,
  emailMoment,
  onEmailMomentChange,
  restrictedEmailFrom,
  dynamicSegmentIdMap,
  onDynamicSegmentIdMapChange,
  errors,
  disabled,
}) => {
  const [forceRender, setForceRender] = useState(1);

  return (
    <FollowUpBodySection
      title="Message"
      key={forceRender}
      subtitle="Modify our template or craft a custom message using our editor. Don’t forget to use merge tags to make your Follow-Up personal."
      slots={{
        titleRight: (
          <FollowUpMessageImport
            id={original_moment_id}
            channel={channel.channel}
            useLexicalEditor={useLexicalEditor}
            originalMomentUseLexicalEditor={originalMomentUseLexicalEditor}
            setDesign={(val) => {
              setDesign(val);
              setForceRender(forceRender + 1);
            }}
            onEmailMomentImport={(val) => {
              onEmailMomentChange(val);
              setForceRender(forceRender + 1);
            }}
            onDynamicSegmentIdMapImport={onDynamicSegmentIdMapChange}
          />
        ),
      }}
    >
      {isChatChannelWithData(channel) && (
        <FollowUpMessageChat
          moment_id={moment_id}
          channel={channel}
          setChannelData={setChannelData}
          mergeTags={mergeTags}
          onChange={setDesign}
          toTarget={toTarget}
          design={design}
          errors={errors}
          disabled={disabled}
        />
      )}

      {isEmailChannelWithData(channel) && !useLexicalEditor && (
        <FollowUpMessageEmailNewsletter
          channel={channel}
          setChannelData={setChannelData}
          mergeTags={mergeTags}
          toTarget={toTarget}
          emailMoment={emailMoment}
          onEmailMomentChange={onEmailMomentChange}
          restrictedEmailFrom={restrictedEmailFrom}
          dynamicSegmentIdMap={dynamicSegmentIdMap}
          onDynamicSegmentIdMapChange={onDynamicSegmentIdMapChange}
          errors={errors}
          disabled={disabled}
        />
      )}

      {isEmailChannelWithData(channel) && useLexicalEditor && (
        <FollowUpMessageEmail
          moment_id={moment_id}
          channel={channel}
          setChannelData={setChannelData}
          mergeTags={mergeTags}
          onChange={setDesign}
          toTarget={toTarget}
          design={design}
          restrictedEmailFrom={restrictedEmailFrom}
          errors={errors}
          disabled={disabled}
        />
      )}

      {isSmsChannelWithData(channel) && (
        <FollowUpMessageSms
          moment_id={moment_id}
          mergeTags={mergeTags}
          onChange={setDesign}
          toTarget={toTarget}
          design={design}
          errors={errors}
          disabled={disabled}
        />
      )}
    </FollowUpBodySection>
  );
};
